// FirmaAdendumPage.tsx

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import {
  getFirmaAdendumEstado,
  getFirmaAdendumPdfName,
  getFirmaAdendumPdfUrl,
  getFirmaAdendumTramites,
  reenviarFirmaAdendumLink,
} from '@/shared/actions/campanas/firma-adendum.actions';
import {
  GeneralTable,
  SingleTableBoxScene,
} from '@/shared/components/ui';
import { useTableFilter } from '@/shared/hooks';
import { useColumnsFirmaAdendum } from '../../hooks';
import {
  ReenviarLinkModal,
  SummaryCards,
  VerEstadoModal,
  VerPdfModal,
} from '../../components';
import {
  buildFirmaAdendumRow,
  type FirmaAdendumRow,
} from '../../utils';
import type { FirmaAdendumTramite } from '../../types';

const PDF_ESTADO_DISPONIBLE = 'documento_guardado';

const FirmaAdendumPage: React.FC = () => {
  ///* table
  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* búsqueda por cédula
  const [cedula, setCedula] = useState('');

  ///* consulta
  const [tramites, setTramites] = useState<FirmaAdendumTramite[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [cedulaBuscada, setCedulaBuscada] = useState<string | undefined>(undefined);

  ///* estado en vivo
  const [estadoRow, setEstadoRow] = useState<FirmaAdendumRow | null>(null);
  const [estadoModalOpen, setEstadoModalOpen] = useState(false);
  const [estadoLoading, setEstadoLoading] = useState(false);
  const [estadoInfo, setEstadoInfo] = useState<{
    estado?: string;
    dato?: unknown;
  }>({});
  const [estadoError, setEstadoError] = useState('');

  ///* reenviar link
  const [reenviarRow, setReenviarRow] = useState<FirmaAdendumRow | null>(null);
  const [reenviarModalOpen, setReenviarModalOpen] = useState(false);
  const [reenviarLoading, setReenviarLoading] = useState(false);
  const [reenviarMessage, setReenviarMessage] = useState('');
  const [reenviarIsError, setReenviarIsError] = useState(false);
  const [reenviarLink, setReenviarLink] = useState<string | null>(null);
  const [reenviarCaducidad, setReenviarCaducidad] = useState<string | null>(null);

  ///* preview pdf
  const [pdfRow, setPdfRow] = useState<FirmaAdendumRow | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState('');

  ///* filas construidas (página actual devuelta por el servidor)
  const rows = useMemo<FirmaAdendumRow[]>(
    () => tramites.map((item, index) => buildFirmaAdendumRow(item, index)),
    [tramites]
  );

  const hasPdf = (row: FirmaAdendumRow): boolean =>
    row.estado === PDF_ESTADO_DISPONIBLE && !!row.rutaPdf;

  const fetchTramites = async (
    currentCedula?: string,
    currentPage = 1,
    currentPageSize = 10,
  ) => {
    setIsLoading(true);
    setErrorMsg('');

    try {
      const response = await getFirmaAdendumTramites(
        currentCedula,
        currentPage,
        currentPageSize,
      );
      setTramites(response?.data ?? []);
      setTotalCount(response?.total ?? 0);
    } catch (error) {
      const err = error as any;
      setTramites([]);
      setTotalCount(0);
      setErrorMsg(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          'No se pudieron obtener los trámites.'
      );
    } finally {
      setIsLoading(false);
      setHasSearched(true);
    }
  };

  const handleSearch = async (cedulaParam?: string, resetPage = true) => {
    const target = cedulaParam ?? cedula;
    if (resetPage) setPagination({ pageIndex: 0, pageSize: 10 });
    setCedulaBuscada(target);
    await fetchTramites(target, 1, 10);
  };

  const handleRefresh = async () => {
    setPagination({ pageIndex: 0, pageSize: 10 });
    await fetchTramites(cedulaBuscada, 1, 10);
  };

  ///* carga inicial: últimos 10 trámites
  useEffect(() => {
    handleSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  ///* refetch al cambiar de página (paginación del servidor)
  useEffect(() => {
    if (!hasSearched) return;
    fetchTramites(cedulaBuscada, pageIndex + 1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  ///* estado en vivo
  const handleVerEstado = async (row: FirmaAdendumRow) => {
    setEstadoRow(row);
    setEstadoModalOpen(true);
    setEstadoInfo({});
    setEstadoError('');
    setEstadoLoading(true);

    try {
      const response = await getFirmaAdendumEstado(row.noTramite);
      setEstadoInfo({
        estado: (response as any)?.estado ?? (response as any)?.data?.estado,
        dato: (response as any)?.data,
      });
    } catch (error) {
      const err = error as any;
      setEstadoError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          'No se pudo consultar el estado en Enext.'
      );
    } finally {
      setEstadoLoading(false);
    }
  };

  ///* preview pdf
  const handleVerPdf = async (row: FirmaAdendumRow) => {
    setPdfRow(row);
    setPdfModalOpen(true);
    setPdfUrl(null);
    setPdfError('');
    setPdfLoading(true);

    try {
      if (!row.rutaPdf) {
        setPdfError('Este trámite no tiene un PDF firmado disponible.');
        return;
      }
      const url = await getFirmaAdendumPdfUrl(row.rutaPdf);
      setPdfUrl(url);
    } catch (error) {
      const err = error as any;
      setPdfError(
        err?.message ||
          'No se pudo cargar el PDF firmado desde el almacenamiento.'
      );
    } finally {
      setPdfLoading(false);
    }
  };

  ///* reenviar link
  const handleOpenReenviar = (row: FirmaAdendumRow) => {
    setReenviarRow(row);
    setReenviarMessage('');
    setReenviarIsError(false);
    setReenviarLink(null);
    setReenviarCaducidad(null);
    setReenviarModalOpen(true);
  };

  const handleReenviar = async (horasExtension?: number) => {
    if (!reenviarRow) return;
    setReenviarLoading(true);
    setReenviarMessage('');
    setReenviarIsError(false);
    setReenviarLink(null);
    setReenviarCaducidad(null);

    try {
      const response = await reenviarFirmaAdendumLink(
        reenviarRow.noTramite,
        reenviarRow.cedula,
        horasExtension
      );
      const link = response?.data?.link ?? null;
      setReenviarLink(link);
      setReenviarCaducidad(response?.data?.fechaCaducidadLink ?? null);
      setReenviarMessage(
        link
          ? 'Link regenerado correctamente. Ya puedes copiarlo y enviarlo por interno.'
          : `Link reenviado correctamente al firmante ${reenviarRow.cedula}.`
      );
      await handleSearch();
    } catch (error) {
      const err = error as any;
      setReenviarIsError(true);
      setReenviarMessage(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          'No se pudo reenviar el link: no se generó una extensión de tiempo.'
      );
    } finally {
      setReenviarLoading(false);
    }
  };

  ///* columns
  const { columnsFirmaAdendum } = useColumnsFirmaAdendum();

  return (
    <SingleTableBoxScene title="Firma Adéndum">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          label="Cédula"
          size="small"
          value={cedula}
          onChange={(e) => setCedula(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="Ej: 1723157580"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => handleSearch()}
          disabled={isLoading}
        >
          Consultar
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          Refrescar
        </Button>
      </Stack>

      {isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Consultando trámites de firma transaccional...
        </Alert>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {!errorMsg && tramites.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SummaryCards data={{ data: tramites }} />
        </Box>
      )}

      {!errorMsg && hasSearched && tramites.length === 0 && !isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No se encontraron trámites para la consulta realizada.
        </Alert>
      )}

      {tramites.length > 0 && (
        <GeneralTable<FirmaAdendumRow>
          columns={columnsFirmaAdendum}
          data={rows}
          isLoading={isLoading}
          enableGlobalFilter
          enableColumnFilters={false}
          enableSorting={false}
          enableColumnActions
          canEdit={false}
          enableEditing
          positionActionsColumn="last"
          showCustomActionBtnSpace
          customActionBtnSpace={(row) => (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Ver estado en vivo (Enext)">
                <IconButton size="small" onClick={() => handleVerEstado(row)}>
                  <VisibilityOutlinedIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reenviar link al firmante">
                <IconButton size="small" onClick={() => handleOpenReenviar(row)}>
                  <SendIcon color="action" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  hasPdf(row)
                    ? 'Ver PDF firmado'
                    : 'PDF no disponible (estado: ' + row.estado + ')'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleVerPdf(row)}
                    disabled={!hasPdf(row)}
                  >
                    <PictureAsPdfIcon
                      color={hasPdf(row) ? 'error' : 'disabled'}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={totalCount}
          getRowId={(row) => row._id}
          muiTableContainerProps={{ sx: { overflowX: 'auto' } }}
        />
      )}

      {!hasSearched && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          Ingresa una cédula y presiona "Consultar" para listar los trámites
          enviados. Si dejas la cédula vacía se muestran los últimos 50.
        </Typography>
      )}

      {/* Modales */}
      <VerEstadoModal
        open={estadoModalOpen}
        onClose={() => {
          setEstadoModalOpen(false);
          setEstadoError('');
        }}
        onRefresh={estadoRow ? () => handleVerEstado(estadoRow) : undefined}
        loading={estadoLoading}
        noTramite={estadoRow?.noTramite ?? ''}
        estado={estadoInfo}
        error={estadoError}
      />

      <VerPdfModal
        open={pdfModalOpen}
        onClose={() => {
          setPdfModalOpen(false);
          setPdfUrl(null);
          setPdfError('');
        }}
        loading={pdfLoading}
        error={pdfError}
        pdfUrl={pdfUrl}
        pdfName={pdfRow?.rutaPdf ? getFirmaAdendumPdfName(pdfRow.rutaPdf) : undefined}
        noTramite={pdfRow?.noTramite ?? ''}
      />

      <ReenviarLinkModal
        open={reenviarModalOpen}
        onClose={() => {
          setReenviarModalOpen(false);
          setReenviarMessage('');
          setReenviarIsError(false);
          setReenviarLink(null);
          setReenviarCaducidad(null);
        }}
        onConfirm={handleReenviar}
        loading={reenviarLoading}
        noTramite={reenviarRow?.noTramite ?? ''}
        cedula={reenviarRow?.cedula ?? ''}
        message={reenviarMessage}
        isError={reenviarIsError}
        link={reenviarLink}
        caducidad={reenviarCaducidad}
      />
    </SingleTableBoxScene>
  );
};

export default FirmaAdendumPage;
